import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { team, PLACEHOLDER_SLOTS } from 'src/data/team';
import type { TeamMember } from 'src/data/team';

// Words that rotate inside the headline highlight (mirrors the homepage
// hero pattern). Keep them themed to a film/production studio.
const ROTATING_WORDS = ['lens', 'eye', 'cut', 'voice', 'frame', 'story'];

// =====================================================================
//  Shared reveal-on-scroll wrapper. Mirrors the pattern used on the
//  Services pages so animations feel consistent network-wide.
// =====================================================================
const Reveal = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // threshold 0: fire as soon as ANY pixel enters the viewport.
        // A non-zero threshold can never be met by an element taller
        // than the viewport (intersectionRatio caps at viewportH/elH),
        // which made tall mobile sections stay invisible. Once shown,
        // disconnect so it never hides again.
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
        filter: visible ? 'blur(0)' : 'blur(6px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter 0.7s ease ${delay}s`,
        willChange: 'opacity, transform, filter'
      }}
    >
      {children}
    </Box>
  );
};

const SectionLabel = ({ children }: { children: string }) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
    <Typography
      sx={{
        fontFamily: '"Roboto Mono", monospace',
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: 2,
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.7)'
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

// =====================================================================
//  Team member card. Real entries get a photo (or initials fallback) and
//  link to /about/team/:slug. Placeholder slots render a dashed "Reserved"
//  box so the layout stays balanced while the roster is filled in.
// =====================================================================
interface TeamCardProps {
  member?: TeamMember;
  placeholderIndex?: number;
}

const TeamCard: FC<TeamCardProps> = ({ member, placeholderIndex }) => {
  const isPlaceholder = !member;

  if (isPlaceholder) {
    return (
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '3 / 4',
          borderRadius: 3,
          border: '1.5px dashed #d4d4d8',
          background: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 3,
          transition: 'all 0.25s ease',
          '&:hover': { borderColor: '#a1a1aa', background: '#f4f4f5' }
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Roboto Mono", monospace',
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            color: '#a1a1aa',
            mb: 1.5
          }}
        >
          Slot {String(placeholderIndex ?? 0).padStart(2, '0')}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: 16,
            fontWeight: 500,
            color: '#71717a',
            mb: 0.5
          }}
        >
          Reserved for future
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: 16,
            fontWeight: 500,
            color: '#71717a'
          }}
        >
          team member
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component={RouterLink}
      href={`/about/team/${member.slug}`}
      sx={{
        position: 'relative',
        aspectRatio: '3 / 4',
        borderRadius: 3,
        overflow: 'hidden',
        background: '#0a0a0b',
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          '& .team-card-photo': { transform: 'scale(1.06)' },
          '& .team-card-name': { color: '#ffffff' }
        }
      }}
    >
      <Box
        className="team-card-photo"
        sx={{
          position: 'absolute',
          inset: 0,
          background: member.photo
            ? `url(${member.photo}) center/cover no-repeat`
            : 'linear-gradient(135deg, #1f2937 0%, #0a0a0b 100%)',
          transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
        }}
      />
      {/* Initials fallback when no photo */}
      {!member.photo && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontSize: 72,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.18)',
              letterSpacing: '-0.04em'
            }}
          >
            {getInitials(member.name)}
          </Typography>
        </Box>
      )}
      {/* Bottom gradient + text */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 50%, transparent 100%)',
          zIndex: 2
        }}
      />
      <Stack
        spacing={0.75}
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: { xs: 2.5, md: 3 },
          zIndex: 3
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Roboto Mono", monospace',
            fontSize: 10.5,
            fontWeight: 500,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.65)'
          }}
        >
          {member.role}
        </Typography>
        <Typography
          className="team-card-name"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: { xs: 20, md: 22 },
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: 'rgba(255,255,255,0.92)',
            transition: 'color 0.25s ease'
          }}
        >
          {member.name}
        </Typography>
        {member.tagline && (
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.7)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {member.tagline}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

// =====================================================================
//  About page section. Hero (light, mirrors homepage hero) + Studio
//  paragraph (light) + Team grid (light, mixed real + placeholder cards).
// =====================================================================
export const AboutPage: FC = () => {
  usePageView();

  // Rotating word state (mirrors HomeHero animation timing exactly)
  const [wordIndex, setWordIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setWordVisible(true);
      }, 280);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  const filledCount = team.length;
  const placeholderCount = Math.max(0, PLACEHOLDER_SLOTS - filledCount);

  return (
    <>
      <Seo
        title="About | Mystic Media Film"
        description="Houston video production agency, web design studio, and digital marketing team. Meet Mystic Media Film and see how we help brands tell stories that drive growth."
      />

      {/* Hero (light, centered, mirrors the homepage hero exactly) */}
      <Box
        sx={{
          background: '#ffffff',
          position: 'relative',
          pt: { xs: '110px', md: '140px' },
          pb: { xs: '60px', md: '80px' }
        }}
      >
        <Container maxWidth="lg">
          {/* Top pill (mirrors the "Based in Katy, TX" pill from HomeHero) */}
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 4, md: 5 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                pl: 0.75,
                pr: 2,
                py: 0.5,
                borderRadius: 999,
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#2563eb',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                ✶
              </Box>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#0a0a0b',
                  letterSpacing: '-0.005em'
                }}
              >
                About <Box component="span" sx={{ fontWeight: 700 }}>Mystic Media Film</Box>
              </Typography>
            </Stack>
          </Stack>

          {/* Headline (single line: static text + rotating word inline) */}
          <Stack spacing={{ xs: 3, md: 4 }} alignItems="center" sx={{ mb: { xs: 5, md: 7 } }}>
            <Typography
              component="h1"
              align="center"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '44px', sm: '64px', md: '88px' },
                fontWeight: 700,
                lineHeight: 1.05,
                letterSpacing: '-0.04em',
                color: '#0a0a0b',
                maxWidth: 1200,
                px: { xs: 2, md: 0 }
              }}
            >
              A small studio with a clear{' '}
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  fontStyle: 'italic',
                  color: '#0a0a0b',
                  whiteSpace: 'nowrap',
                  opacity: wordVisible ? 1 : 0,
                  transform: wordVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'all 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: { xs: '-4%', md: '-6%' },
                    height: { xs: '22%', md: '24%' },
                    background:
                      'linear-gradient(180deg, transparent 0%, rgba(147, 197, 253, 0.55) 0%, rgba(96, 165, 250, 0.45) 100%)',
                    borderRadius: 1,
                    zIndex: -1,
                    transformOrigin: 'left center',
                    animation: wordVisible
                      ? 'highlightSweepAbout 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards'
                      : 'none'
                  },
                  '@keyframes highlightSweepAbout': {
                    '0%': { transform: 'scaleX(0)' },
                    '100%': { transform: 'scaleX(1)' }
                  }
                }}
              >
                {ROTATING_WORDS[wordIndex]}.
              </Box>
            </Typography>
            <Typography
              align="center"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: 16, md: 19 },
                fontWeight: 400,
                lineHeight: 1.55,
                letterSpacing: '-0.01em',
                color: '#52525b',
                maxWidth: 640
              }}
            >
              Houston-based video production.{' '}
              <Box component="span" sx={{ color: '#0a0a0b', fontWeight: 600 }}>
                One team handling concept, filming, and post for brands that want the story told
                well.
              </Box>
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Team grid */}
      <Box sx={{ background: '#fafafa', py: { xs: 14, md: 20 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ md: 'flex-end' }}
              justifyContent="space-between"
              spacing={3}
              sx={{ mb: { xs: 6, md: 9 } }}
            >
              <Stack spacing={2.5}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box sx={{ width: 24, height: 1, background: '#71717a' }} />
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: 2,
                      textTransform: 'uppercase',
                      color: '#52525b'
                    }}
                  >
                    The team
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontSize: { xs: 36, md: 56 },
                    fontWeight: 700,
                    lineHeight: 1.08,
                    letterSpacing: '-0.03em',
                    color: '#0a0a0b'
                  }}
                >
                  The people behind
                  <br />
                  the work.
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: 15, md: 16 },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: '#52525b',
                  maxWidth: 380
                }}
              >
                Click any card to read a member's full background.
              </Typography>
            </Stack>
          </Reveal>

          <Reveal delay={0.05}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: { xs: 3, md: 3.5 }
              }}
            >
              {team.map((m) => (
                <TeamCard key={m.slug} member={m} />
              ))}
              {Array.from({ length: placeholderCount }).map((_, i) => (
                <TeamCard key={`placeholder-${i}`} placeholderIndex={filledCount + i + 1} />
              ))}
            </Box>
          </Reveal>
        </Container>
      </Box>
    </>
  );
};
